import { Icon, Text } from "@ttc3k/trekker";
import { EnumSurveyQuestionType, OrderByIdQuery } from "gql/generated";
import { HtmlContent } from "shared/components/molecules/HtmlContent";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import { formatAddressSearchResponse } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import {
  getBooleanResponseDisplay,
  isMultiOptionBooleanQuestion,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionCheckbox";
import {
  isQuestionVisible,
  isToggleOn,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionVisibility";
import {
  ChatBubbleQuestion,
  CheckSquareSolid,
  MinusSquare,
} from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { ReservationSection } from "./ReservationSection";

type SurveryResponsesProps = {
  surveyResponse: NonNullable<OrderByIdQuery["orderById"]>["surveyResponse"];
};

export const SurveryResponses = ({ surveyResponse }: SurveryResponsesProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.SURVEY_RESPONSES",
  });

  const getResponseDisplay = (
    response: NonNullable<
      NonNullable<OrderByIdQuery["orderById"]>["surveyResponse"]
    >["responses"][0],
    index: number,
  ) => {
    const question = response?.questionData.name.en;
    const isWhereaboutsSearch =
      response?.questionData.type === EnumSurveyQuestionType.WhereaboutsSearch;
    const isRichText =
      response?.questionData.type === EnumSurveyQuestionType.RichText;
    const isAddressSearch =
      response?.questionData.type === EnumSurveyQuestionType.AddressSearch;
    const answer = isWhereaboutsSearch
      ? (response?.response[1] ?? response?.response[0])
      : isAddressSearch
        ? formatAddressSearchResponse(response?.response ?? [])
        : response?.questionData.type === EnumSurveyQuestionType.Toggle
          ? isToggleOn(response?.response)
            ? "Yes"
            : "No"
          : response?.questionData.type === EnumSurveyQuestionType.Boolean
            ? getBooleanResponseDisplay(
                response.questionData,
                response?.response ?? [],
              )
            : response?.response[0];
    const richHtml = response?.response[0] ?? "";

    const isBoolean =
      response?.questionData.type === EnumSurveyQuestionType.Boolean ||
      response?.questionData.type === EnumSurveyQuestionType.Toggle;
    const isFollowUp = !!response?.questionData.parentQuestion;
    const isSkipped = !isQuestionVisible(
      response?.questionData,
      surveyResponse?.responses ?? [],
    );
    const isMultiOptionBoolean = isMultiOptionBooleanQuestion(
      response?.questionData,
    );

    return (
      <Flex
        key={index}
        flexDir={isBoolean ? "row" : "column"}
        gap={isBoolean ? "600" : "50"}
        px="300"
        pl={isFollowUp ? "600" : "300"}
        py="250"
        {...(index > 0 && {
          borderTop: "1px solid",
          borderColor: "border.light",
        })}
        w="full"
      >
        <Text visual="bodySemiBold" w="full" whiteSpace="pre-wrap">
          {question}
        </Text>
        {isSkipped ? (
          <Text visual="bodyRegular" color="text.light" w="full">
            {t("SKIPPED")}
          </Text>
        ) : isBoolean ? (
          !isMultiOptionBoolean ? (
            <Icon
              flexShrink={0}
              Element={answer === "Yes" ? CheckSquareSolid : MinusSquare}
              w="20px"
              h="20px"
            />
          ) : (
            <Text visual="bodyRegular" w="full" whiteSpace="pre-wrap">
              {answer}
            </Text>
          )
        ) : isRichText ? (
          !isLexicalHtmlEmpty(richHtml) ? (
            <HtmlContent html={richHtml} className="w-full" />
          ) : (
            <Text visual="bodyRegular" w="full" whiteSpace="pre-wrap">
              —
            </Text>
          )
        ) : (
          <Text visual="bodyRegular" w="full" whiteSpace="pre-wrap">
            {answer}
          </Text>
        )}
      </Flex>
    );
  };

  if (!surveyResponse || surveyResponse.responses.length === 0) return null;

  return (
    <ReservationSection icon={ChatBubbleQuestion} title={t("TITLE")}>
      <Flex
        flexDir="column"
        gap="0"
        bgColor="var(--wa-colors-ink-100)"
        border="2px solid"
        borderColor="border.light"
        borderRadius="100"
      >
        {surveyResponse.responses.map((response, index) =>
          getResponseDisplay(response, index),
        )}
      </Flex>
    </ReservationSection>
  );
};
